import styled, { css, keyframes } from 'styled-components'

const focusRing = css`
  &:focus-visible {
    outline: 2px solid rgb(var(--m-color-brand));
    outline-offset: 2px;
  }
`

export const StyledList = styled.nav`
  display: flex;
  flex-direction: column;
  gap: var(--m-spacing-4);
  min-height: 0;
`

export const StyledListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--m-spacing-2);
`

/** The one filled button in the list: starting over is what the list is for. */
export const StyledNewChat = styled.button`
  display: inline-flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: var(--m-spacing-2);
  padding: 10px var(--m-spacing-3);
  border: 0;
  border-radius: 10px;
  background-color: rgb(var(--m-color-primary-100));
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-3);
  font-weight: var(--m-font-weight-bold);
  color: rgb(var(--m-color-neutral-0));
  cursor: pointer;
  transition: background-color 120ms ease;

  m-icon {
    --m-icon-color: rgb(var(--m-color-brand));
  }

  &:hover {
    background-color: rgba(var(--m-color-primary-100), 0.88);
  }

  ${focusRing}
`

export const StyledGroups = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--m-spacing-4);
`

export const StyledGroupLabel = styled.h3`
  margin: 0 0 var(--m-spacing-1);
  padding: 0 var(--m-spacing-3);
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-2);
  font-weight: var(--m-font-weight-medium);
  line-height: var(--m-line-height-5);
  color: rgb(var(--m-color-text-placeholder));
`

export const StyledRows = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
`

/**
 * Rename and delete show on hover and keyboard focus. They sit over the end of
 * the row instead of beside the title, so the title gets the whole row until
 * then. Touch screens always show them.
 */
export const StyledRowActions = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 0 var(--m-spacing-1) 0 var(--m-spacing-6);
  border-radius: 0 var(--m-border-radius-m) var(--m-border-radius-m) 0;
  background: linear-gradient(
    90deg,
    rgba(var(--m-color-background-primary), 0),
    rgb(var(--m-color-background-primary)) var(--m-spacing-5)
  );
  opacity: 0;

  @media (hover: none) {
    position: static;
    padding-left: 0;
    background: none;
    opacity: 1;
  }
`

export const StyledRow = styled.li<{ $active: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  border-radius: var(--m-border-radius-m);
  background-color: ${({ $active }) =>
    $active ? 'rgb(var(--m-color-background-primary))' : 'transparent'};
  box-shadow: ${({ $active }) =>
    $active
      ? 'inset 3px 0 0 rgb(var(--m-color-brand)), 0 1px 2px rgba(var(--m-color-primary-100), 0.08)'
      : 'none'};
  transition: background-color 120ms ease;

  &:hover,
  &:focus-within {
    background-color: rgb(var(--m-color-background-primary));
  }

  &:hover ${StyledRowActions}, &:focus-within ${StyledRowActions} {
    opacity: 1;
  }
`

export const StyledSelect = styled.button`
  display: flex;
  flex: 1;
  min-width: 0;
  padding: var(--m-spacing-2) var(--m-spacing-3);
  border: 0;
  border-radius: var(--m-border-radius-m);
  background: none;
  text-align: left;
  cursor: pointer;

  ${focusRing}
`

export const StyledTitle = styled.span<{ $active: boolean }>`
  overflow: hidden;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-3);
  font-weight: ${({ $active }) =>
    $active ? 'var(--m-font-weight-bold)' : 'var(--m-font-weight-regular)'};
  line-height: var(--m-line-height-5);
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $active }) =>
    $active ? 'rgb(var(--m-color-primary-100))' : 'rgb(var(--m-color-text-secondary))'};
`

export const StyledIconButton = styled.button`
  position: relative;
  display: inline-flex;
  padding: 6px;
  border: 0;
  border-radius: var(--m-border-radius-s);
  background: none;
  color: rgb(var(--m-color-text-placeholder));
  cursor: pointer;

  &:hover {
    color: rgb(var(--m-color-primary-100));
    background-color: rgb(var(--m-color-background-secondary));
  }

  /* On touch the buttons are always shown: a bigger target, 44px tall without a taller row. */
  @media (hover: none) {
    padding: var(--m-spacing-2);

    &::after {
      content: '';
      position: absolute;
      inset: min(0px, (100% - 44px) / 2) 0;
    }
  }

  ${focusRing}
`

export const StyledNote = styled.p`
  margin: 0;
  padding: 0 var(--m-spacing-3);
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-3);
  line-height: var(--m-line-height-5);
  color: rgb(var(--m-color-text-tertiary));
`

export const StyledTextButton = styled.button`
  align-self: flex-start;
  margin-left: var(--m-spacing-3);
  padding: var(--m-spacing-1) 0;
  border: 0;
  background: none;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-3);
  font-weight: var(--m-font-weight-medium);
  color: rgb(var(--m-color-text-link));
  cursor: pointer;

  ${focusRing}
`

const shimmer = keyframes`
  0% { background-position: -240px 0; }
  100% { background-position: 240px 0; }
`

export const StyledSkeleton = styled.div`
  height: 36px;
  margin-bottom: 2px;
  border-radius: var(--m-border-radius-m);
  background: linear-gradient(
    90deg,
    rgba(var(--m-color-background-tertiary), 0.5) 25%,
    rgb(var(--m-color-background-tertiary)) 37%,
    rgba(var(--m-color-background-tertiary), 0.5) 63%
  );
  background-size: 480px 100%;
  animation: ${shimmer} 1.4s ease infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`
